import { Component, OnDestroy, OnInit, computed, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LocaleService } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import {
  Subject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ManureCategory } from 'src/app/shared/models/manure-category.model';
import { ManureType, manureTypeOptions } from 'src/app/shared/models/manure-type.model';
import { Month } from 'src/app/shared/models/month.model';

import { Tabs } from '../../overview/models/overview.model';
import * as OverviewActions from '../../overview/store/actions/overview.actions';
import {
  selectCurrentTab,
  selectOfferPaginationOptions,
} from '../../overview/store/selectors/overview.selector';
import { ManureFilters } from './../../../../shared/models/manure-filters.model';
import { PageAndSize } from './../../overview/models/paginated-overview.model';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  standalone: false,
})
export class FilterComponent implements OnInit, OnDestroy {
  private readonly locale = inject(LocaleService);
  private readonly store = inject(Store);

  formGroupInitialState = {
    manureCategory: new FormControl(undefined),
    manureType: new FormControl(undefined),
    amountFrom: new FormControl(undefined, [Validators.min(35), Validators.pattern('[0-9]+')]),
    amountTo: new FormControl(undefined, [Validators.min(36), Validators.pattern('[0-9]+')]),
    isHomogeneous: new FormControl(undefined),
    distance: new FormControl(undefined),
    desiredDeliveryPeriodFrom: new FormControl(undefined),
    desiredDeliveryPeriodTo: new FormControl(undefined),
  };

  destroy$ = new Subject<void>();
  formGroup: FormGroup = new FormGroup(this.formGroupInitialState);

  selectedManureCategory$ = this.formGroup.controls.manureCategory.valueChanges.pipe(
    startWith(undefined),
    tap((manureCategory: ManureCategory) => {
      if (
        manureCategory &&
        !manureTypeOptions[manureCategory].includes(this.formGroup.controls.manureType.value)
      ) {
        this.formGroup.controls.manureType.setValue('');
        this.formGroup.controls.manureType.updateValueAndValidity();
      }
    }),
    shareReplay(1),
  );

  selectedManureType$ = this.formGroup.controls.manureType.valueChanges.pipe(
    startWith(''),
    tap((manureType) => {
      const manureCategory = this.getManureCategoryByType(manureType);
      if (manureCategory) {
        this.formGroup.controls.manureCategory.setValue(manureCategory);
        this.formGroup.controls.manureCategory.updateValueAndValidity();
      }
    }),
  );

  filteredManureTypes$ = combineLatest([
    this.selectedManureCategory$,
    this.selectedManureType$,
  ]).pipe(
    filter(([_, manureType]) => typeof manureType === 'string'),
    map(([manureCategory, manureType]: [ManureCategory, string]) => {
      if (!manureCategory) {
        return Object.values(ManureType)
          .map((x) => ({ value: x, stringValue: this.translateManureType(x) }))
          .filter((x) => x.stringValue.toLowerCase().includes(manureType.toLowerCase()));
      }

      return manureTypeOptions[manureCategory]
        .map((x) => ({ value: x, stringValue: this.translateManureType(x) }))
        .filter((x) => x.stringValue.toLowerCase().includes(manureType.toLowerCase()));
    }),
  );

  manureCategories = Object.values(ManureCategory);
  months = Object.values(Month);

  /**
   * Computed signal that returns a stable formatter function for the autocomplete display.
   * Prevents template from recreating the function on every change detection cycle.
   */
  displayManureTypeFormatterSignal = computed(() => {
    return (manureType?: ManureType) => (manureType ? this.translateManureType(manureType) : '');
  });

  ngOnInit(): void {
    this.store
      .select(selectCurrentTab)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.formGroup.updateValueAndValidity();
      });
    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(500),
        withLatestFrom(
          this.store.select(selectOfferPaginationOptions),
          this.store.select(selectCurrentTab),
        ),
      )
      .subscribe(([filters, paginationOptions, currentTab]: [ManureFilters, PageAndSize, Tabs]) => {
        this.store.dispatch(OverviewActions.setFilters({ filters }));
        if (currentTab === Tabs.manureOffers) {
          this.store.dispatch(OverviewActions.loadOffers({ paginationOptions }));
        } else if (currentTab === Tabs.manureRequests) {
          this.store.dispatch(OverviewActions.loadRequests({ paginationOptions }));
        }
      });
  }

  ngOnDestroy(): void {
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  getManureCategoryByType(manureType: ManureType): ManureCategory | undefined {
    for (const category of Object.keys(manureTypeOptions)) {
      if (manureTypeOptions[category as keyof typeof manureTypeOptions].includes(manureType)) {
        return category as ManureCategory;
      }
    }
    return undefined;
  }

  translateManureType(manureType: ManureType): string {
    return this.locale.translate(`manureTypes.${manureType}`);
  }

  /**
   * Returns the stable formatter function from the computed signal.
   * Called as displayManureType() in the template to pass the formatter to [displayWith].
   */
  displayManureType() {
    return this.displayManureTypeFormatterSignal();
  }

  resetFilters() {
    this.formGroup.reset();
  }
}
